import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import CloseRounded from '@mui/icons-material/CloseRounded';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import { Link as RouterLink } from 'react-router-dom';
import { useFeature } from '../../ui/FeaturesProvider';
import { downloadQueueXlsx, fetchQueue, type FetchOpts } from '../../services/contactOptimizationService';
import type { QueueResponse, QueueRow, QueueRowStatus } from '../../types/contactOptimization';
import SettingsPageHeader from '../settings/components/SettingsPageHeader';
import ChartSectionHeader from './ChartSectionHeader';
import ContactOptimizationNav from './ContactOptimizationNav';
import { RefreshProvider, useRefreshRegistration, useReportFreshness } from './RefreshController';
import ExplainerNote, { ExplainerToggle } from './ExplainerNote';
import { buildContactOptGridSx, makeContactOptGridToolbar } from './contactOptGridKit';

// Export has its own icon button in the header row right above the grid
// (matching Timing/Plan's tables) instead of the grid's own toolbar --
// one obvious download affordance instead of two.
const QueueGridToolbar = makeContactOptGridToolbar('queue', { showExport: false });

// Ten, not the shared [25, 50, 100, 250]. Queue rows carry two lines each
// (lead label above a masked id), so a page of fifty is something you
// scroll rather than something you read.
const QUEUE_PAGE_SIZE = 10;
const QUEUE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

function num(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return value.toFixed(digits);
}

const STATUS_META: Record<QueueRowStatus, { label: string; color: 'success' | 'info' | 'warning' | 'default'; dimmed: boolean }> = {
  dialing_now: { label: 'Dialing now', color: 'success', dimmed: false },
  priority: { label: 'Priority', color: 'info', dimmed: false },
  held: { label: 'Held', color: 'warning', dimmed: true },
  blocked: { label: 'Blocked', color: 'default', dimmed: true },
};

function StatusChip({ status }: { status: QueueRowStatus }) {
  const meta = STATUS_META[status];
  return <Chip label={meta.label} size="small" color={meta.color === 'default' ? undefined : meta.color} variant={meta.color === 'default' ? 'outlined' : 'filled'} />;
}

function MetricTile({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  const content = (
    <Box sx={(t) => ({ p: 1.25, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.sm}px`, minWidth: 120 })}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow>{content}</Tooltip> : content;
}

/** A labelled cluster of tiles. The page shows two kinds of number that
 * were previously explained apart in prose -- how much work there is, and
 * what that work should return. Grouping them under headings lets the
 * layout carry that distinction instead of a paragraph apologising for it. */
function MetricGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: 'block', mb: 0.75, fontWeight: 700, fontSize: '0.62rem', letterSpacing: 0.7, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
        {children}
      </Stack>
    </Box>
  );
}

const STATUS_FILTERS: { key: 'all' | QueueRowStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'dialing_now', label: 'Dialing now' },
  { key: 'priority', label: 'Priority' },
  { key: 'held', label: 'Held' },
  { key: 'blocked', label: 'Blocked' },
];

export default function QueuePage() {
  return (
    <RefreshProvider>
      <QueuePageContent />
    </RefreshProvider>
  );
}

function QueuePageContent() {
  const theme = useTheme();
  const feature = useFeature('contact_optimization');
  const [queue, setQueue] = React.useState<QueueResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<'all' | QueueRowStatus>('all');
  const [showStatusHelp, setShowStatusHelp] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState<QueueRow | null>(null);
  const gridApiRef = useGridApiRef();

  // The working file for the customer, and the page's primary action. Built
  // server-side from the whole queue rather than exported from the grid,
  // which only ever holds what is currently filtered and on screen. The
  // grid's own CSV export was removed when this landed: two download
  // buttons that produced different files was the confusing part, not the
  // format.
  const [xlsxBusy, setXlsxBusy] = React.useState(false);
  const [xlsxError, setXlsxError] = React.useState<string | null>(null);
  const handleDownloadXlsx = React.useCallback(() => {
    setXlsxBusy(true);
    setXlsxError(null);
    downloadQueueXlsx()
      .catch((e: unknown) => setXlsxError(e instanceof Error ? e.message : 'Failed to download workbook'))
      .finally(() => setXlsxBusy(false));
  }, []);

  const load = React.useCallback((opts?: FetchOpts) => {
    setLoading(true);
    setError(null);
    return fetchQueue(opts)
      .then(setQueue)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load Queue'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (!feature.enabled) return;
    load();
  }, [feature.enabled, load]);

  useRefreshRegistration(React.useCallback(() => load({ refresh: true }), [load]));
  useReportFreshness(queue?.computed_at);

  const gridSx = React.useMemo(
    () => buildContactOptGridSx(theme, { dimmedRowClass: 'queue-row-dimmed' }),
    [theme],
  );

  const columns = React.useMemo<GridColDef<QueueRow>[]>(
    () => [
      {
        field: 'lead_label',
        headerName: 'Lead',
        description: "A first name, if we managed to recover one (this only ever comes from an outbound text greeting; there is no name, email, or phone number stored anywhere in this data). If we don't have a name, this shows the product and state we'd recommend instead. Hover over the row for the anonymized lead ID and more context.",
        minWidth: 190,
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={`${params.row.masked_id}${params.row.lead_sublabel ? ` \u00b7 ${params.row.lead_sublabel}` : ''}`} arrow>
            <Box sx={{ lineHeight: 1.25, py: 0.5, minWidth: 0 }}>
              <Typography variant="body2" noWrap>{params.row.lead_label}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {params.row.lead_sublabel ?? params.row.masked_id}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        // Second column, matching the workbook's own layout. This is the
        // only field that joins a row on this screen to a row in Regal, so
        // anyone reconciling the two needs it visible rather than buried in
        // a hover -- the masked id beside the lead name is for reading, not
        // for looking anything up.
        field: 'hubspot_contact_id',
        headerName: 'Contact ID',
        description: 'HubSpot contact ID — the id used to match this lead in Regal and in the downloaded workbook.',
        minWidth: 150,
        flex: 0.7,
        renderCell: (params) => (
          <Typography
            variant="caption"
            noWrap
            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'text.secondary' }}
          >
            {params.row.hubspot_contact_id}
          </Typography>
        ),
      },
      { field: 'state', headerName: 'State', minWidth: 70, flex: 0.4 },
      {
        field: 'call_hour_label',
        headerName: 'Call at',
        description: 'When to call this lead, in their own local time. "Now" means this hour. Held/blocked rows show when the calling window next opens instead, or a dash if there is no next time to give (for example, if this lead has already been called the maximum number of times allowed within the last 7 days).',
        minWidth: 210,
        flex: 1,
        valueFormatter: (value: string | null) => value ?? '\u2013',
      },
      {
        field: 'pickup_pct',
        headerName: 'Pickup',
        type: 'number',
        minWidth: 90,
        flex: 0.5,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => pct(value),
      },
      {
        field: 'value_score',
        headerName: 'Lead score',
        description: "This lead's quality score, the same score used elsewhere in the CRM (our customer relationship management system). Higher means a better track record of turning into a paying customer.",
        type: 'number',
        minWidth: 100,
        flex: 0.5,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => (value == null ? '\u2013' : value.toLocaleString()),
      },
      {
        field: 'deal_rate',
        headerName: 'Deal rate',
        description: "If we reach this lead on the phone, this is our estimated chance they go on to become a paying customer. It's calibrated from this lead's own lead score, checked against how similar leads have actually converted in the past. This is different from the Pickup column, which is about whether they answer the phone at all.",
        type: 'number',
        minWidth: 90,
        flex: 0.5,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => pct(value),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        flex: 0.6,
        renderCell: (params) => <StatusChip status={params.value as QueueRowStatus} />,
      },
      { field: 'reason', headerName: 'Why', minWidth: 300, flex: 1.8 },
      {
        field: 'talk_headline',
        headerName: 'What to say',
        description: "A suggested opener for this call: an existing appointment or quote wins first, then a recent text reply, then attempt history. Hover for the full detail plus useful facts (budget, tobacco use, marital status). Not a live transcript summary — Queue doesn't re-read past call recordings.",
        minWidth: 220,
        flex: 1.3,
        renderCell: (params) => {
          const facts = params.row.talk_facts ?? [];
          const factsText = facts.length > 0 ? ` (${facts.join(', ')})` : '';
          return (
            <Tooltip title={`${params.row.talk_detail}${factsText}`} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled', display: 'inline-block', maxWidth: '100%' }}
              >
                {params.row.talk_headline}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'attempt_number',
        headerName: 'Attempt #',
        type: 'number',
        minWidth: 90,
        flex: 0.4,
        align: 'right',
        headerAlign: 'right',
      },
    ],
    [],
  );

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <ContactOptimizationNav active="queue" />
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !queue) {
    return (
      <Box>
        <ContactOptimizationNav active="queue" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <ContactOptimizationNav active="queue" />
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!queue) return null;

  if (!queue.available) {
    return (
      <Box>
        <ContactOptimizationNav active="queue" />
        <SettingsPageHeader description="What is being dialed now, why, and which agent should take it." />
        <Alert severity="info">
          {queue.note ?? 'No trained model yet.'}{' '}
          <RouterLink to="/predictive-models/contact-optimization">Go to Predictive Models to train one</RouterLink>.
        </Alert>
      </Box>
    );
  }

  const m = queue.metrics as QueueResponse['metrics'] & {
    total_eligible_population?: number;
    dialing_now?: number;
    held_for_later?: number;
    outside_call_window?: number;
    attempt_cap_blocked?: number;
    random_sample_pct?: number;
    capacity_now?: number;
    agents_on_now?: number;
    global_dials_per_agent_hour?: number | null;
    expected_connect_rate_dialing_now?: number | null;
    expected_connects_this_hour?: number | null;
    expected_deals_this_hour?: number | null;
    avg_attempt_number_dialing_now?: number | null;
    reference_hour_label?: string;
    reference_is_now?: boolean;
    reference_is_tomorrow?: boolean;
  };
  const agentsOnNow = m.agents_on_now ?? 0;
  // Queue answers for the current hour whenever anyone is working, and
  // otherwise for the next hour someone is. Which one it landed on has to
  // be visible: "expected connects this hour" quietly meaning a different
  // hour than the reader assumes is worse than showing nothing.
  const rolledForward = m.reference_is_now === false;
  const referenceLabel = m.reference_hour_label ?? '';
  const forecastSuppressed = m.reference_is_tomorrow === true;

  const filteredRows = statusFilter === 'all' ? queue.rows : queue.rows.filter((r) => r.status === statusFilter);
  const statusCounts: Record<'all' | QueueRowStatus, number> = {
    all: queue.rows.length,
    dialing_now: 0,
    priority: 0,
    held: 0,
    blocked: 0,
  };
  for (const r of queue.rows) statusCounts[r.status] += 1;

  return (
    <Box>
      <ContactOptimizationNav active="queue" />
      <SettingsPageHeader description="Queue answers 'should this lead be called right now, and by whom', for every lead worth calling, ranked from most to least valuable — not a full schedule for the whole day." />

      <Paper elevation={0} sx={(t) => ({ p: 2.5, border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px`, mb: 2, bgcolor: t.tokens.surface.raised })}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
              {rolledForward
                ? `Next shift \u2014 ${referenceLabel}${forecastSuppressed ? ' tomorrow' : ''}`
                : 'This hour'}
            </Typography>
            {agentsOnNow > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, maxWidth: '72ch', lineHeight: 1.5 }}>
                {rolledForward
                  ? `Nobody is on the floor right now, so this shows ${referenceLabel}${forecastSuppressed ? ' tomorrow' : ' today'} instead \u2014 ${agentsOnNow} agent${agentsOnNow === 1 ? '' : 's'} on shift, room for about ${(m.capacity_now ?? 0).toLocaleString()} calls.`
                  : `${agentsOnNow} agent${agentsOnNow === 1 ? '' : 's'} on shift, room for about ${(m.capacity_now ?? 0).toLocaleString()} calls. Everything below is a model estimate for these specific leads, not a record of what happened.`}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={xlsxBusy ? <CircularProgress size={14} color="inherit" /> : <FileDownloadRounded sx={{ fontSize: 18 }} />}
            onClick={handleDownloadXlsx}
            disabled={xlsxBusy}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {xlsxBusy ? 'Preparing\u2026' : 'Download working file'}
          </Button>
        </Stack>

        {xlsxError && (
          <Alert severity="error" onClose={() => setXlsxError(null)} sx={{ mb: 2 }}>
            {xlsxError}
          </Alert>
        )}

        {agentsOnNow === 0 ? (
          // Reached only when the roster has nobody on at any hour of the
          // day -- the ordinary overnight case rolls forward instead. Worth
          // saying plainly, because it means the roster itself is empty
          // rather than that this happens to be a quiet hour.
          <Alert severity="warning" sx={{ mb: 1 }}>
            The roster has nobody on shift at any hour, so there is no capacity to plan against. The ranked list
            below still shows every lead worth calling, and the working file can be downloaded now.
          </Alert>
        ) : (
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }} useFlexGap>
            <MetricGroup label="Being dialled">
              <MetricTile label="Dialling now" value={String(m.dialing_now ?? 0)} tooltip={`Room to call this hour: ${m.capacity_now ?? 0} calls, from ${m.agents_on_now ?? 0} named agents each averaging about ${m.global_dials_per_agent_hour?.toFixed(1) ?? '\u2013'} calls per hour. Based on our best estimate of today's schedule, not a confirmed shift roster.`} />
              <MetricTile label="Held for later" value={String(m.held_for_later ?? 0)} tooltip="Either a better hour is coming up later today, or there's no room to call them this hour \u2014 check each row's Why column to see which." />
            </MetricGroup>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {forecastSuppressed ? (
              <Box sx={{ maxWidth: '46ch' }}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mb: 0.75, fontWeight: 700, fontSize: '0.62rem', letterSpacing: 0.7, textTransform: 'uppercase' }}
                >
                  What these calls should produce
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block' }}>
                  Not shown for tomorrow. Tomorrow&rsquo;s queue will include leads that have not arrived yet, so any
                  connect or deal count worked out now would be built on a population we know is short by about a
                  day&rsquo;s intake. The ranking below is still a true picture of the backlog as it stands.
                </Typography>
              </Box>
            ) : (
            <MetricGroup label="What these calls should produce">
              <MetricTile
                label="Expected connect rate"
                value={pct(m.expected_connect_rate_dialing_now)}
                tooltip="The average chance of an answer across the leads being dialled this hour. A model estimate for these specific leads, not a historical average. Measured against real outcomes it currently runs about 12% low, so treat it as an ordering rather than a promise."
              />
              <MetricTile
                label="Expected connects"
                value={num(m.expected_connects_this_hour)}
                tooltip="Each lead's own chance of answering, added up across everything being dialled this hour. For example, 20 calls that each have a 1-in-10 chance of being answered comes to about 2 connects."
              />
              <MetricTile
                label="Expected deals"
                value={num(m.expected_deals_this_hour, 2)}
                tooltip="The same sum as expected connects, but each lead is also multiplied by its own chance of becoming a paying customer once reached. This is the number the ranking is actually trying to make as large as possible."
              />
            </MetricGroup>
            )}
          </Stack>
        )}

        {/* The rarely-interesting counts. Real, and worth being able to find,
            but they were competing with the numbers people actually act on
            when all eleven sat in one row. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, pt: 1.5, borderTop: (t) => `1px solid ${t.tokens.border.subtle}`, lineHeight: 1.6 }}>
          From <strong>{(m.total_eligible_population ?? 0).toLocaleString()}</strong> eligible leads
          {` \u00b7 ${(m.outside_call_window ?? 0).toLocaleString()} outside their calling window`}
          {` \u00b7 ${(m.attempt_cap_blocked ?? 0).toLocaleString()} at the attempt cap`}
          {m.avg_attempt_number_dialing_now != null ? ` \u00b7 averaging attempt #${num(m.avg_attempt_number_dialing_now)}` : ''}
          {` \u00b7 ${pct(m.random_sample_pct, 0)} random sample`}
        </Typography>
      </Paper>

      <Paper elevation={0} sx={(t) => ({ border: `1px solid ${t.tokens.border.subtle}`, borderRadius: `${t.ds.radius.md}px`, mb: 2, overflow: 'hidden' })}>
        <Box sx={{ p: 2 }}>
          <ChartSectionHeader
            title="Queue"
            subtitle="Every lead worth calling this hour, ranked from most to least valuable. Times are in each lead's own local time, not the agent's."
            actions={
              <ExplainerToggle
                title="How this queue works, and what to know before acting on it"
                open={showStatusHelp}
                onClick={() => setShowStatusHelp((v) => !v)}
              />
            }
          />
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map((f) => (
              <Chip
                key={f.key}
                label={`${f.label} (${statusCounts[f.key].toLocaleString()})`}
                size="small"
                variant={statusFilter === f.key ? 'filled' : 'outlined'}
                color={statusFilter === f.key ? 'primary' : 'default'}
                onClick={() => setStatusFilter(f.key)}
              />
            ))}
          </Stack>

          <ExplainerNote title="How this queue works, and what to know before acting on it" open={showStatusHelp}>
        {`**Statuses:**

- **Dialing now** — an agent is free this hour, this lead made the cut, and a specific agent has been assigned (see the Agent column).
- **Priority** — a brand-new lead (created today, never called yet) that jumps to the front of the line regardless of score; calling fast matters enough that these skip the ranking entirely.
- **Held** — worth calling, but not being called this exact hour, either because there's no room left this hour or because a better hour is coming up later today. The Why column says which.
- **Blocked** — cannot be called right now no matter what: it's outside the legal calling window, or we've already called them the maximum number of times allowed within the last 7 days.

**Columns:**

- **Lead** — a first name, if we managed to recover one (this only ever comes from an outbound text greeting; there is no name, email, or phone number stored anywhere in this data). If we don't have a name, this shows the product and state we'd recommend instead. Hover over the row for the anonymized lead ID.
- **State** — the US state this lead is in, which determines their local time zone and which insurance products they qualify for.
- **Call at** — when to call this lead, in their own local time. "Now" means this hour; a held or blocked row shows when the next legally allowed window opens instead.
- **Pickup** — our estimated chance that this lead answers the phone, specifically during this hour.
- **Lead score** — this lead's underlying quality score, the same score used elsewhere in the CRM (our customer relationship management system). Higher means a better track record of turning into paying customers.
- **Deal rate** — turns that score into an estimated chance of becoming a paying customer *if they answer the phone*, based on how similar leads have converted historically. This is the number that actually decides where a lead sits in the ranking, not the raw lead score.
- **Status** — see the "Statuses" list above.
- **Why** — the plain-English reason this lead got its status, e.g. why it's being held instead of dialed, or why it's blocked.
- **What to say** — a suggested opener, e.g. "confirm the appointment" or "answer their text first," picked from whatever's most relevant about this specific lead. Hover for the full reasoning plus a few useful facts (budget, tobacco use, marital status) pulled straight from their file.
- **Agent** — which agent on shift should take this call. Leads are handed out to on-shift agents in turn, one at a time in rank order, cycling back to the first agent once every agent has had a turn, so the workload stays even; only set for "Dialing now" rows. This assumes every agent is legally allowed to take a lead from any state (confirmed with the customer), and it does not yet account for some agents naturally calling faster than others.
- **Attempt #** — how many times we've already called this lead. A lead can only be called a limited number of times within a rolling time window; once it hits that limit it becomes "Blocked" until enough time has passed.

**Before you act on this:**
${queue.notes.map((note) => `- ${note}`).join('\n')}

The filters and search above only change what you see here. The working file at the top of the page always contains the whole queue. Click any row for the full reasoning behind it.`}
          </ExplainerNote>
        </Box>

        {/* Header and grid are one section, not two stacked cards. The
            filters above act on the rows below, so a border between them
            read as a boundary that is not really there. */}
        <Box sx={(t) => ({ width: '100%', borderTop: `1px solid ${t.tokens.border.subtle}` })}>
          <DataGrid
            apiRef={gridApiRef}
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.hubspot_contact_id}
            getRowClassName={(params) => (STATUS_META[params.row.status as QueueRowStatus].dimmed ? 'queue-row-dimmed' : '')}
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedRow(params.row as QueueRow)}
            sortingMode="client"
            paginationMode="client"
            // autoHeight rather than a fixed container: at ten rows a fixed
            // height either leaves dead space or scrolls inside a scroll.
            autoHeight
            pageSizeOptions={QUEUE_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: QUEUE_PAGE_SIZE, page: 0 } } }}
            slots={{ toolbar: QueueGridToolbar }}
            localeText={{ toolbarQuickFilterPlaceholder: 'Search lead, state, reason\u2026' }}
            sx={{ ...gridSx, border: 0, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>

        <Box sx={(t) => ({ px: 2, py: 1.25, borderTop: `1px solid ${t.tokens.border.subtle}`, bgcolor: t.tokens.surface.sunken ?? 'transparent' })}>
          <Typography variant="caption" color="text.secondary">
            {filteredRows.length.toLocaleString()} of {queue.rows_total?.toLocaleString() ?? queue.rows.length} ranked leads
            {statusFilter !== 'all' ? ` (filtered to ${STATUS_META[statusFilter].label.toLowerCase()})` : ''}
            {queue.model_version ? ` \u00b7 prediction model v${queue.model_version}` : ''}.
          </Typography>
        </Box>
      </Paper>

      <LeadDetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />
    </Box>
  );
}

/**
 * Same idea as the old Dialing Opportunity v1 roster's click-to-expand row
 * (`components/build2/LeadRosterSection.tsx`): the grid stays scannable at
 * a glance, and the full "why" -- timing, agent, talk track -- lives one
 * click away instead of being crammed into tooltips or extra columns.
 */
function LeadDetailDrawer({ row, onClose }: { row: QueueRow | null; onClose: () => void }) {
  return (
    <Drawer anchor="right" open={!!row} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      {row && (
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{row.lead_label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {`${row.masked_id}${row.lead_sublabel ? ` \u00b7 ${row.lead_sublabel}` : ''} \u00b7 ${row.state}`}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}><CloseRounded sx={{ fontSize: 18 }} /></IconButton>
          </Stack>
          <Box sx={{ mb: 2 }}><StatusChip status={row.status} /></Box>

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>Why this status</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>{row.reason}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>When to call</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.call_hour_label ?? '\u2013'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block', mt: 0.25 }}>
                {row.current_pct != null
                  ? `${pct(row.current_pct)} pickup chance this hour, vs. ${pct(row.best_pct)} at their best hour (${row.best_hour_label}, their time).`
                  : `Best hour to call, if not blocked: ${row.best_hour_label} (${pct(row.best_pct)} pickup chance), their time.`}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>What to say</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.talk_headline}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, mt: 0.25 }}>{row.talk_detail}</Typography>
              {row.talk_facts.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                  {row.talk_facts.map((f) => (
                    <Chip key={f} size="small" variant="outlined" label={f} />
                  ))}
                </Stack>
              )}
            </Box>


            <Divider />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Score breakdown</Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Lead score (CRM)</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{row.value_score == null ? '\u2013' : row.value_score.toLocaleString()}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Deal rate if reached</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{pct(row.deal_rate)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">Attempt number</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{row.attempt_number}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}
